import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, FileText, Download, Upload, CheckCircle2, XCircle } from 'lucide-react';
import { publicPrestationDevisApi } from '../../lib/api';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { SignatureDevisEnLigne, ContexteSignature } from './SignatureDevisEnLigne';

interface DevisInfo {
    company_name: string;
    logo_url: string | null;
    brand_color: string;
    label: string;
    montant: string;
    file_name: string;
    has_document: boolean;
    etat: 'en_attente' | 'recu' | 'decline' | 'clos' | 'expire';
    received_at: string | null;
    lost_reason: string;
    // Devis sans engagement signé : adresse pour fixer le rendez-vous.
    reservation_url?: string | null;
    // Signature en ligne (lot S1) : null tant que l'établissement n'a pas de devis à faire signer.
    signature?: (ContexteSignature & { signe_en_ligne: boolean }) | null;
    // Facilité de paiement proposée une fois le devis signé.
    paiement?: { options: { id: string; label: string; aide: string; quand: { id: string; label: string; aide: string }[] }[]; choix: { id: string; methode: string; at: string | null; engagement_at?: string | null } | null; engagement_pour?: string[] };
    // Contrat signé à domicile (démarchage) : dates fixées par la loi, null sinon.
    demarchage?: { paiement_possible_le: string | null; intervention_possible_le: string | null; fin_retractation: string | null; paiement_bloque: boolean; mandat_differe: boolean } | null;
}

const jourLong = (jour: string | null | undefined) => (jour ? new Date(`${jour}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : '');

// « Comment souhaitez-vous régler ? » : affiché une fois le devis signé. Le choix est enregistré, il ne déclenche aucun paiement.
function ChoixPaiement({ token, paiement, demarchage }: { token: string; paiement: NonNullable<DevisInfo['paiement']>; demarchage?: DevisInfo['demarchage'] }) {
    const [moyen, setMoyen] = React.useState(paiement.choix?.methode || (paiement.options.length === 1 ? paiement.options[0].id : ''));
    const [quand, setQuand] = React.useState(paiement.choix?.id || '');
    const [enregistre, setEnregistre] = React.useState(!!paiement.choix);
    const [mandat, setMandat] = React.useState<{ etat: string; raison?: string; le?: string } | null>(
        demarchage?.mandat_differe ? { etat: 'differe', le: demarchage.paiement_possible_le || undefined } : null);
    const [engagement, setEngagement] = React.useState(!!paiement.choix?.engagement_at);
    const [envoi, setEnvoi] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    if (!paiement.options.length) return null;
    const optionMoyen = paiement.options.find((o) => o.id === moyen);
    const optionQuand = optionMoyen?.quand.find((q) => q.id === quand);
    const exigeEngagement = !!optionQuand && (paiement.engagement_pour || []).includes(optionQuand.id);
    const valider = async () => {
        if (!optionMoyen || !optionQuand || (exigeEngagement && !engagement)) return;
        setEnvoi(true); setErreur('');
        try {
            const r: any = await publicPrestationDevisApi.choisirPaiement(token, { choix: quand, methode: moyen, engagement: exigeEngagement ? engagement : undefined });
            if (r?.error) throw new Error(r.error);
            setMandat(r?.mandat || null);
            setEnregistre(true);
        } catch (e: any) { setErreur(e?.message || "Votre choix n'a pas pu être enregistré."); }
        finally { setEnvoi(false); }
    };
    if (enregistre && optionMoyen && optionQuand) {
        return (
            <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1">
                <p className="text-sm font-bold text-content">Paiement : {optionMoyen.label}, {optionQuand.label.toLowerCase()}</p>
                <p className="text-xs text-content-muted">
                    {mandat?.etat === 'differe' ? `C'est noté. Le formulaire pour renseigner votre IBAN vous sera envoyé par e-mail le ${jourLong(mandat.le)} : pour un contrat signé à domicile, rien ne peut vous être demandé avant.`
                        : mandat?.etat === 'envoye' ? "C'est noté. Nous venons de vous envoyer un e-mail pour renseigner votre IBAN en toute sécurité."
                        : mandat?.etat === 'deja_envoye' ? "C'est noté. L'e-mail pour renseigner votre IBAN vous a déjà été envoyé : pensez à regarder vos courriers indésirables."
                        : mandat?.etat === 'actif' ? "C'est noté. Votre autorisation de prélèvement est déjà enregistrée."
                        : mandat?.etat === 'erreur' ? "C'est noté. Nous vous enverrons nous-mêmes le formulaire IBAN dans les meilleurs délais."
                        : "C'est noté, nous vous recontactons pour la suite."}
                </p>
                <button type="button" onClick={() => setEnregistre(false)} className="text-xs text-content-muted hover:text-content underline">Modifier mon choix</button>
            </div>
        );
    }
    const carte = (actif: boolean) => 'block rounded-xl border p-3 cursor-pointer transition-colors ' + (actif ? 'border-brand-500 bg-brand-500/5' : 'border-border hover:border-brand-500/40');
    return (
        <div className="space-y-3">
            <p className="text-sm font-bold text-content">Comment souhaitez-vous régler ?</p>
            {paiement.options.some((o) => o.id === 'sepa' && o.quand.some((q) => q.id.startsWith('mensuel'))) && (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2.5 text-xs text-emerald-800 dark:text-emerald-300 space-y-0.5">
                    <p className="font-bold text-sm">Facilité de paiement, sans frais</p>
                    <p>Le prélèvement SEPA vous permet de régler en plusieurs fois, jusqu'à 24 mois même pour un contrat d'un an. Les échéances convenues sont dues à leur date.</p>
                </div>
            )}
            {paiement.options.length > 1 && (
                <div className="space-y-2">
                    {paiement.options.map((o) => (
                        <label key={o.id} className={carte(moyen === o.id)}>
                            <span className="flex items-start gap-2.5">
                                <input type="radio" name="moyen" checked={moyen === o.id} onChange={() => { setMoyen(o.id); setQuand(''); }} className="accent-brand-600 mt-1" />
                                <span>
                                    <span className="block text-sm font-bold text-content">{o.label}</span>
                                    <span className="block text-xs text-content-muted">{o.aide}</span>
                                </span>
                            </span>
                        </label>
                    ))}
                </div>
            )}
            {optionMoyen && (
                <div className="space-y-2">
                    <p className="text-xs font-bold text-content-muted uppercase tracking-wider">Quand ?</p>
                    {optionMoyen.quand.map((q) => (
                        <label key={q.id} className={carte(quand === q.id)}>
                            <span className="flex items-start gap-2.5">
                                <input type="radio" name="quand" checked={quand === q.id} onChange={() => setQuand(q.id)} className="accent-brand-600 mt-1" />
                                <span>
                                    <span className="block text-sm font-bold text-content">{q.label}</span>
                                    {q.aide && <span className="block text-xs text-content-muted">{q.aide}</span>}
                                </span>
                            </span>
                        </label>
                    ))}
                </div>
            )}
            {exigeEngagement && (
                <label className="flex items-start gap-2.5 rounded-xl border border-border p-3 text-xs text-content cursor-pointer">
                    <input type="checkbox" checked={engagement} onChange={(e) => setEngagement(e.target.checked)} className="accent-brand-600 mt-0.5" />
                    <span>Je m'engage à régler l'intégralité des échéances convenues, quoi qu'il arrive, y compris si le contrat prend fin avant le dernier règlement.</span>
                </label>
            )}
            <button type="button" onClick={valider} disabled={!optionQuand || envoi || (exigeEngagement && !engagement)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-black disabled:opacity-50">
                {envoi ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Valider mon choix
            </button>
            {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}
        </div>
    );
}

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; }
};

/**
 * Page publique de signature du devis pour le Suivi de prestation (Prestataire) — même gabarit que
 * DevisSignaturePage.tsx côté Suivi privatisation, mais code séparé et appelant l'API dédiée
 * (/prestation-devis) : aucun partage entre les deux systèmes de pipeline.
 *
 * Elle ne fait avancer le dossier jusqu'à « Devis signé » : le dépôt est enregistré, mais c'est le
 * prestataire qui valide manuellement depuis le pipeline.
 */
export const PrestationDevisSignaturePage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [info, setInfo] = React.useState<DevisInfo | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');
    const [fichier, setFichier] = React.useState<File | null>(null);
    const [envoi, setEnvoi] = React.useState(false);
    const [etat, setEtat] = React.useState<DevisInfo['etat'] | null>(null);
    const [refusOuvert, setRefusOuvert] = React.useState(false);
    const [motif, setMotif] = React.useState('');
    // Le dépôt d'un exemplaire papier reste possible, mais en second : la signature en ligne est la voie
    // normale depuis le lot S1.
    const [depotOuvert, setDepotOuvert] = React.useState(false);
    const [signeEnLigne, setSigneEnLigne] = React.useState(false);
    const [urlRdv, setUrlRdv] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!token) return;
        publicPrestationDevisApi.getInfo(token)
            .then((d: any) => { setInfo(d); setEtat(d?.etat || null); })
            .catch((e: any) => setErreur(e?.message || 'Lien invalide ou expiré.'))
            .finally(() => setLoading(false));
    }, [token]);

    const deposer = async () => {
        if (!token || !fichier) return;
        setEnvoi(true);
        setErreur('');
        try {
            const data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ''));
                reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
                reader.readAsDataURL(fichier);
            });
            await publicPrestationDevisApi.uploadSigned(token, { file_name: fichier.name, mime_type: fichier.type || 'application/pdf', data });
            setEtat('recu');
        } catch (e: any) {
            setErreur(e?.message || "Votre document n'a pas pu être envoyé.");
        } finally {
            setEnvoi(false);
        }
    };

    const decliner = async () => {
        if (!token || !motif.trim()) return;
        setEnvoi(true);
        setErreur('');
        try {
            await publicPrestationDevisApi.decline(token, motif.trim());
            setEtat('decline');
        } catch (e: any) {
            setErreur(e?.message || "Votre réponse n'a pas pu être enregistrée.");
        } finally {
            setEnvoi(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-surface-app"><Loader2 size={32} className="animate-spin text-content-muted" /></div>;
    }

    if (!info) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4 text-center">
                <p className="text-content font-bold mb-1">Lien invalide</p>
                <p className="text-content-muted text-sm">{erreur || "Ce lien n'est plus valide."}</p>
            </div>
        );
    }

    const sig = info.signature || null;
    const logo = resolveLogoUrl(info.logo_url);
    const champ = 'w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500';

    return (
        <div className="min-h-screen bg-surface-app py-10 px-4">
            <div className="max-w-lg mx-auto space-y-5">
                <div className="text-center space-y-2">
                    {logo && <img src={logo} alt={info.company_name} className="h-14 mx-auto object-contain" />}
                    <h1 className="text-xl font-black text-content">{info.company_name}</h1>
                </div>

                <div className="bg-surface border border-border rounded-2xl p-5 space-y-4">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Devis</p>
                        <p className="text-lg font-bold text-content mt-0.5">{info.label}</p>
                        {info.montant && <p className="text-sm text-content-muted">Montant : <span className="font-bold text-content">{info.montant}</span></p>}
                    </div>

                    {info.has_document && (
                        <a href={`/api/prestation-devis/${token}/document`} target="_blank" rel="noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border hover:border-brand-500/50 text-sm font-bold text-content transition-colors">
                            <Download size={15} /> Télécharger le devis
                        </a>
                    )}

                    {etat === 'recu' ? (
                        <div className="space-y-3">
                            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                                <CheckCircle2 size={18} className="text-green-500 shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-bold text-content">
                                        {signeEnLigne || sig?.signe_en_ligne ? 'Votre devis est signé. C\'est fait !' : 'Votre devis signé nous est bien parvenu.'}
                                    </p>
                                    <p className="text-xs text-content-muted mt-0.5">
                                        {info.received_at ? `Reçu le ${fmtDate(info.received_at)}. ` : ''}
                                        {signeEnLigne || sig?.signe_en_ligne
                                            ? ((urlRdv || info.reservation_url) ? 'Votre prix est validé, sans engagement. Choisissez maintenant la date de votre rendez-vous. Une copie signée vient de vous être envoyée par email.' : 'Une copie signée, avec son certificat, vient de vous être envoyée par email.')
                                            : 'Nous revenons vers vous très vite. Rien d\'autre à faire de votre côté.'}
                                    </p>
                                </div>
                            </div>
                            {(urlRdv || info.reservation_url) && (
                                <a href={urlRdv || info.reservation_url || '#'}
                                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-500 text-sm font-bold text-white transition-colors">
                                    Fixer mon rendez-vous
                                </a>
                            )}
                            {info.demarchage?.paiement_bloque && info.demarchage.paiement_possible_le && (
                                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-0.5">
                                    <p className="font-bold text-sm text-content">Contrat signé à domicile</p>
                                    <p className="text-xs text-content-muted">Rien ne vous sera demandé à payer avant le {jourLong(info.demarchage.paiement_possible_le)}. Vous pouvez vous rétracter jusqu'au {jourLong(info.demarchage.fin_retractation)}, avec le formulaire joint à votre devis signé.</p>
                                </div>
                            )}
                            {info.paiement && <ChoixPaiement token={token || ''} paiement={info.paiement} demarchage={info.demarchage} />}
                            {(signeEnLigne || sig?.signe_en_ligne) && (
                                <a href={`/api/prestation-devis/${token}/signed-document`} target="_blank" rel="noreferrer"
                                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border hover:border-brand-500/50 text-sm font-bold text-content transition-colors">
                                    <Download size={15} /> Télécharger mon devis signé
                                </a>
                            )}
                        </div>
                    ) : etat === 'decline' ? (
                        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-surface-soft border border-border">
                            <XCircle size={18} className="text-content-muted shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-bold text-content">Nous avons bien noté que vous ne donniez pas suite.</p>
                                <p className="text-xs text-content-muted mt-0.5">Merci de nous l'avoir dit — et à une prochaine fois.</p>
                            </div>
                        </div>
                    ) : etat === 'expire' ? (
                        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-surface-soft border border-border">
                            <XCircle size={18} className="text-content-muted shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-bold text-content">Le délai pour répondre à ce devis est dépassé.</p>
                                <p className="text-xs text-content-muted mt-0.5">Merci de refaire une demande de devis : nous vous répondrons avec plaisir.</p>
                            </div>
                        </div>
                    ) : etat === 'clos' ? (
                        <p className="text-sm text-content-muted">
                            Ce devis n'attend plus de réponse de votre part. Contactez votre interlocuteur si vous avez une question.
                        </p>
                    ) : (
                        <>
                            {sig && (
                                <SignatureDevisEnLigne token={token || ''} ctx={sig}
                                    onSigned={(recu, resa) => {
                                        setSigneEnLigne(true); setEtat('recu'); setUrlRdv(resa || null); setInfo({ ...info, received_at: recu });
                                        // Relecture : les dates d'un contrat signé à domicile ne sont connues qu'après la signature.
                                        if (token) publicPrestationDevisApi.getInfo(token).then((d: any) => { if (d && !d.error) setInfo(d); }).catch(() => {});
                                    }} />
                            )}

                            {sig && !depotOuvert ? (
                                <button type="button" onClick={() => setDepotOuvert(true)} className="w-full text-xs text-content-muted hover:text-content underline pt-1">
                                    Je préfère imprimer, signer à la main et déposer mon exemplaire
                                </button>
                            ) : (
                            <div className="space-y-2 pt-1">
                                <p className="text-xs font-bold text-content">Déposer votre exemplaire signé</p>
                                <label className="block cursor-pointer">
                                    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => setFichier(e.target.files?.[0] || null)} />
                                    <div className={`border-2 border-dashed rounded-xl p-5 flex flex-col items-center justify-center gap-1 transition-colors ${fichier ? 'border-brand-500/60 bg-brand-500/5' : 'border-border hover:border-brand-500/40'}`}>
                                        {fichier ? (
                                            <>
                                                <FileText size={22} className="text-brand-600 dark:text-brand-400" />
                                                <p className="text-xs font-bold text-content">{fichier.name}</p>
                                                <p className="text-[10px] text-content-muted">{(fichier.size / 1024).toFixed(0)} Ko</p>
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={22} className="text-content-muted" />
                                                <p className="text-xs font-bold text-content">Choisir le fichier</p>
                                                <p className="text-[10px] text-content-muted">PDF ou photo — 10 Mo maximum</p>
                                            </>
                                        )}
                                    </div>
                                </label>
                                <button type="button" onClick={deposer} disabled={!fichier || envoi}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-black disabled:opacity-50">
                                    {envoi ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Envoyer mon devis signé
                                </button>
                            </div>
                            )}

                            <div className="pt-2 border-t border-border">
                                {!refusOuvert ? (
                                    <button type="button" onClick={() => setRefusOuvert(true)} className="w-full text-xs text-content-muted hover:text-content underline">
                                        Je ne donne pas suite
                                    </button>
                                ) : (
                                    <div className="space-y-2">
                                        <p className="text-xs font-bold text-content">Pouvez-vous nous dire pourquoi ?</p>
                                        <p className="text-[11px] text-content-muted">Une phrase suffit. Cela nous aide vraiment à nous améliorer.</p>
                                        <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} placeholder="Budget, date indisponible, autre prestataire..." className={`${champ} resize-none`} />
                                        <div className="flex gap-2">
                                            <button type="button" onClick={() => { setRefusOuvert(false); setMotif(''); }} disabled={envoi}
                                                className="flex-1 px-3 py-2 rounded-xl border border-border text-xs font-bold text-content-muted hover:text-content">
                                                Annuler
                                            </button>
                                            <button type="button" onClick={decliner} disabled={!motif.trim() || envoi}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-border text-xs font-bold text-content hover:bg-surface-soft disabled:opacity-50">
                                                {envoi ? <Loader2 size={13} className="animate-spin" /> : null} Envoyer ma réponse
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}
                </div>

                <p className="text-[10px] text-content-muted text-center">Ce lien vous est personnel. Ne le transmettez pas.</p>
            </div>
        </div>
    );
};
